import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ApiError, dev, get, patch, post } from "../api/client";
import { useLoad, usePoll } from "../api/hooks";
import type { CalibrationView, ClipList, Player, PlayerMerge, Px, Roster, Video, VideoSegment } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { Gate } from "../components/Gate";
import { PlayerRows, PlayerRowsLoading, twoNames } from "../components/PlayerCards";
import { PlayersStatus } from "../components/PlayersStatus";
import { Shell } from "../components/Shell";
import { RunReviewPanel } from "../components/RunReview";
import { YoutubeRow } from "../components/Youtube";
import { Dot, ErrorLine, Sheet, VidFrame, useToast } from "../components/ui";
import { prefetchClipUrls } from "../lib/clipPrefetch";
import { fmtClock, fmtDate, fmtDuration, fmtSpan, plural } from "../lib/format";
import { STAGES, videoParts, videoStatus } from "../lib/status";
import { identityBusy, resolveRoster } from "../lib/roster";
import { downloadName, exportLabel, useExport } from "../lib/exports";
import { useIsDesktop } from "../lib/device";
import { planName } from "./Videos";

/** /videos/:id — 04 (before the clips are ready) or 05 (match page) by state. */
export default function VideoPage() {
  const { id = "" } = useParams();
  const { plan } = useAuth();
  const video = useLoad(() => get<{ video: Video }>(`/v1/videos/${id}`).then((r) => r.video), [id]);
  const v = video.data;
  const status = v ? videoStatus(v, plan) : null;
  usePoll(video.reload, 5000, !!status && !status.terminal);
  if (video.error) return <Shell back="/"><div className="p mt24"><ErrorLine error={video.error} /></div></Shell>;
  if (!v || !status) return <Shell back="/"><div className="p mt24 tiny">Loading…</div></Shell>;
  // An operator looks at a held run's clips to decide on it (video.review is theirs only).
  return v.state === "ready" || (v.state === "held" && v.review) ? <MatchPage v={v} reload={video.reload} /> : <StatusPage v={v} reload={video.reload} />;
}

function StatusPage({ v, reload }: { v: Video; reload: () => Promise<void> }) {
  const { me, plan } = useAuth();
  const desktop = useIsDesktop();
  const [toast, say] = useToast();
  const s = videoStatus(v, plan);
  const [advancing, setAdvancing] = useState(false);
  const advance = async () => {
    setAdvancing(true);
    try {
      await dev.advance(v.id);
      await reload();
    } finally {
      setAdvancing(false);
    }
  };
  const meta = [fmtDate(v.created_at), fmtDuration(v.duration_ms), v.segment ? `part ${fmtSpan(v.segment)}` : ""].filter(Boolean).join(" · ");
  const line = s.stage === -1 ? s.explain : `${s.detail ? `Expected ${s.detail}. ` : ""}We email you when the clips are ready.`;
  const parts = videoParts(v);
  return (
    <Shell back="/" right={<span />}>
      <div className="p" style={{ paddingTop: desktop ? 48 : 8, paddingBottom: 40 }} data-testid="status-page" data-state={v.state}>
        <div className="tiny">{v.title} · {meta}</div>
        <h1 className="mt6">{s.headline}</h1>
        <p className={desktop ? "sub mt16" : "sub mt10"} style={{ maxWidth: 560 }}>{line}</p>
        {s.stage >= 0 && (
          <>
            <div className="stage-rail mt32">
              {STAGES.map((n, i) => (
                <div key={n}><Dot kind={i < s.stage ? "ok" : i === s.stage ? "on" : ""} /><span style={i === s.stage ? { fontWeight: 500 } : i > s.stage ? { color: "var(--ink3)" } : undefined}>{n}</span></div>
              ))}
            </div>
            <div className="rail-d mt48">
              {STAGES.map((n, i) => <div key={n} className={i < s.stage ? "done" : i === s.stage ? "on" : ""}><i /><span>{n}</span></div>)}
            </div>
          </>
        )}
        {s.stage >= 0 && s.explain && <p className={desktop ? "tiny mt32" : "tiny mt24"} style={{ maxWidth: 560 }}>{s.explain}</p>}
        {me?.features?.youtube && <YoutubeRow videoId={v.id} where="status" desktop={desktop} say={say} className="mt32" />}
        {s.action === "plans" && plan === "free" && (
          <div className="mt24"><Link className="btn dark wide" to="/plans">Move to the front · Basic</Link></div>
        )}
        {parts.length > 0 && <Parts v={v} parts={parts} reload={reload} />}
        {s.action === "retry" && <div className="mt24"><Link className="btn ghost wide" to="/upload">Upload another video</Link></div>}
        {s.stage >= 1 && s.stage < 3 && <CalibrationFrame v={v} />}
        {dev.enabled && !s.terminal && (
          <p className="tiny mt24"><button className="u" onClick={advance} disabled={advancing} data-testid="dev-advance">{advancing ? "Advancing…" : "Advance to the next stage (dev, fake pipeline)"}</button></p>
        )}
      </div>
      {toast}
    </Shell>
  );
}

/**
 * The parts of a refused upload where the camera stayed still, one row each:
 * start, end, length, and a button that has that part analysed instead
 * (the video is checked again from the upload, that part only).
 */
function Parts({ v, parts, reload }: { v: Video; parts: VideoSegment[]; reload: () => Promise<void> }) {
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<Error | null>(null);
  // one clock for every row, as the message above writes it: 0:01:10 beside 0:54:24
  const hours = parts.some((p) => p.end_ms >= 3_600_000);
  const choose = async (seg: VideoSegment, i: number) => {
    setBusy(i);
    setError(null);
    try {
      await post(`/v1/videos/${v.id}/segment`, seg);
      await reload();
    } catch (e) {
      setError(e as Error);
      setBusy(null);
    }
  };
  return (
    <div className="mt32" style={{ maxWidth: 560 }} data-testid="video-parts">
      <h3>{parts.length === 1 ? "The part where the camera stayed still" : "Parts where the camera stayed still"}</h3>
      <div className="list mt10">
        {parts.map((seg, i) => (
          <div key={seg.start_ms} data-testid="video-part">
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>{fmtSpan(seg, hours)}</div>
              <div className="tiny">{fmtDuration(seg.end_ms - seg.start_ms)}</div>
            </div>
            <button className="btn dark sm" onClick={() => choose(seg, i)} disabled={busy !== null} data-testid="use-part">{busy === i ? "Starting…" : "Analyse this part"}</button>
          </div>
        ))}
      </div>
      <p className="tiny mt10">Only the part you choose is analysed. It counts as one video.</p>
      <ErrorLine error={error} />
    </div>
  );
}

/**
 * The frame the pitch is marked on: plain until the pitch is found, then
 * with the pitch drawn from the ground config the analysis uses. Nothing
 * renders when the video has no frame or it fails to load.
 */
function CalibrationFrame({ v }: { v: Video }) {
  const calibrated = v.calibration_state === "accepted" || v.calibration_state === "auto" || v.calibration_state === "reused";
  const cal = useLoad(() => get<CalibrationView>(`/v1/videos/${v.id}/calibration`), [v.id, calibrated]);
  const [failed, setFailed] = useState(false);
  const frame = cal.data?.frame;
  if (!frame || failed) return null;
  const [w, h] = frame.image_size ?? [16, 9];
  const pitch = frame.image_size ? cal.data?.pitch : null;
  const pts = (ps: Px[]) => ps.map((p) => p.join(",")).join(" ");
  return (
    <div className="frame mt24" style={{ aspectRatio: `${w} / ${h}` }} data-testid="calibration-frame">
      <img src={frame.url} alt="Calibration frame" onError={() => setFailed(true)} />
      {pitch ? (
        <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true" data-testid="pitch-overlay">
          <polygon points={pts(pitch.outline)} />
          {pitch.creases.map((c, i) => <polyline key={i} points={pts(c)} />)}
        </svg>
      ) : null}
      <span className="tag">Calibration frame{v.calibration_frame_ms != null ? ` · ${fmtClock((v.segment?.start_ms ?? 0) + v.calibration_frame_ms)}` : ""}</span>
      {pitch ? <span className="tr">Pitch marked</span> : null}
    </div>
  );
}

function MatchPage({ v, reload }: { v: Video; reload: () => Promise<void> }) {
  const held = v.state === "held";
  const nav = useNavigate();
  const desktop = useIsDesktop();
  const { me, plan } = useAuth();
  const clips = useLoad(() => get<ClipList>(`/v1/videos/${v.id}/deliveries`), [v.id]);
  const rosterApi = useLoad(() => get<Roster>(`/v1/videos/${v.id}/players`), [v.id]);
  const items = clips.data?.items ?? [];
  const live = items.filter((c) => !c.flagged);
  const roster = resolveRoster(rosterApi.data);
  // The players are found by a second GPU job after the clips are ready: poll while it runs.
  usePoll(rosterApi.reload, 10_000, identityBusy(roster));
  const exp = useExport();
  const [toast, say] = useToast();
  const downloadAll = useCallback(async () => {
    if (exp.state.phase === "ready") return window.open(exp.state.url, "_blank");
    await exp.start(live.map((c) => c.id), "mp4", downloadName(roster, { batsman: null, bowler: null }, "all"));
  }, [exp, live, roster]);
  useEffect(() => {
    if (exp.state.phase === "ready") say("Your clips are ready to download.");
    if (exp.state.phase === "failed") say(exp.state.message);
  }, [exp.state, say]);
  const selectable = clips.data?.players.selectable ?? plan !== "free";
  const [gate, setGate] = useState(false);
  // A player's picture opens their clips, which play one after another; on Free it opens the Basic sheet.
  const openPlayer = (p: Player) => (selectable ? nav(`/videos/${v.id}/clips?${p.role}=${p.id}`) : setGate(true));
  useEffect(() => {
    prefetchClipUrls([live[0]?.clip.playback_url, live[1]?.clip.playback_url]);
  }, [live]);
  const warmPlayer = useCallback((p: Player) => {
    if (!selectable) return;
    const theirs = live.filter((c) => {
      const a = roster.assign(c);
      return p.role === "batsman" ? a.batsman_id === p.id : a.bowler_id === p.id;
    });
    prefetchClipUrls([theirs[0]?.clip.playback_url, theirs[1]?.clip.playback_url, live[0]?.clip.playback_url]);
  }, [selectable, live, roster]);
  const rename = async (p: Player, name: string | null) => {
    try {
      rosterApi.set(await post<Roster>(`/v1/videos/${v.id}/players/${p.id}/name`, { name }));
    } catch (e) {
      say((e as Error).message);
    }
  };
  // A merge or an undo the API refuses (another tab changed the players first) shows the players as they are now.
  const merge = async (p: Player, into: Player) => {
    const [from, to] = twoNames(p, into);
    try {
      rosterApi.set(await post<Roster>(`/v1/videos/${v.id}/players/${p.id}/merge`, { into: into.id }));
      say(`${from} merged into ${to}.`);
      return true;
    } catch (e) {
      say((e as Error).message);
      await rosterApi.reload();
      return false;
    }
  };
  const undoMerge = async (m: PlayerMerge) => {
    const [from, to] = twoNames(m.player, m.into);
    try {
      rosterApi.set(await post<Roster>(`/v1/videos/${v.id}/players/merges/${m.id}/undo`));
      say(`${from} and ${to} are two players again.`);
      return true;
    } catch (e) {
      say((e as Error).message);
      await rosterApi.reload();
      return false;
    }
  };
  const busy = identityBusy(roster);
  const found = roster.batsmen.length + roster.bowlers.length;
  const managedTeams = (me?.teams ?? []).filter((t) => t.role === "manager");
  const [teamSheetOpen, setTeamSheetOpen] = useState(false);
  const [pickedTeamId, setPickedTeamId] = useState<string>(v.team?.id ?? "");
  const [confirmClearMove, setConfirmClearMove] = useState(false);
  const [teamBusy, setTeamBusy] = useState(false);

  const changeVideoTeam = async (confirmClear = false) => {
    if (teamBusy) return;
    setTeamBusy(true);
    try {
      await patch(`/v1/videos/${v.id}`, {
        team_id: pickedTeamId || null,
        ...(confirmClear ? { confirm_clear_assignments: true } : {}),
      });
      setTeamSheetOpen(false);
      setConfirmClearMove(false);
      await Promise.all([reload(), rosterApi.reload(), clips.reload()]);
      say(pickedTeamId ? "Match linked to team." : "Match moved to personal.");
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.details?.reason === "HAS_ASSIGNMENTS") {
        setConfirmClearMove(true);
      } else {
        say((e as Error).message);
      }
    } finally {
      setTeamBusy(false);
    }
  };

  const players = busy ? (
    <PlayerRowsLoading />
  ) : found > 0 ? (
    <PlayerRows
      roster={roster}
      videoId={v.id}
      onOpen={openPlayer}
      onWarm={warmPlayer}
      onRename={rename}
      onMerge={merge}
      onUndo={undoMerge}
      onRosterChange={(r) => rosterApi.set(r)}
      say={say}
    />
  ) : null;
  // What the rows do not say: the stage's state in one line, and on Free the way to Basic.
  const status = (
    <>
      <PlayersStatus videoId={v.id} roster={roster} selectable={selectable} onChange={rosterApi.reload} className={desktop ? "mt16" : "mt24"} />
      {!selectable && found === 0 && <div className="mt16"><button className={`btn ghost${desktop ? "" : " full"}`} onClick={() => setGate(true)} data-testid="players-upsell">Pick a player on Basic</button></div>}
    </>
  );
  const summary = (
    <>
      <div className="cap">Players</div>
      {found > 0 && (
        <>
          <div className="mt10" style={{ fontSize: 24, fontWeight: 500, lineHeight: 1.2 }} data-testid="players-found">{plural(roster.batsmen.length, "batsman", "batsmen")} and {plural(roster.bowlers.length, "bowler")}</div>
          <p className="sub mt10">Assign a player to their @handle (or type a name) and every clip of theirs carries it. Click a picture to play their clips one after another.</p>
        </>
      )}
      {status}
    </>
  );
  // The uploaded video's post to YouTube, under the buttons (components/Youtube.tsx); nothing when none was asked for.
  const youtube = me?.features?.youtube ? <YoutubeRow videoId={v.id} where="match" desktop={desktop} say={say} className="mt16" /> : null;
  const player = (
    <VidFrame height={desktop ? 420 : 210} tag={`Whole match · ${plural(live.length, "clip")}`} play poster={v.thumbnail_url} ball={[52, 60]} track onClick={() => nav(`/videos/${v.id}/clips`)} />
  );
  const actions = (
    <>
      <button className="btn dark" onClick={() => nav(`/videos/${v.id}/clips`)} data-testid="play-all">Play all</button>
      {!held && <button className="btn ghost" onClick={downloadAll} disabled={exp.state.phase === "requesting" || exp.state.phase === "preparing" || live.length === 0} data-testid="download-all">{exportLabel(exp.state, `Download · ${live.length}`)}</button>}
    </>
  );
  return (
    <Shell back="/" right={<button className="r" onClick={() => say("Sharing arrives with public moments.")}>Share</button>}>
      <div className="p" style={{ paddingTop: desktop ? 32 : 4, paddingBottom: 40 }} data-testid="match-page">
        <div className="row" style={{ justifyContent: "flex-start", gap: 8, flexWrap: "wrap" }}>
          <span className="tiny">{[fmtDate(v.created_at), fmtDuration(v.duration_ms), v.segment ? `part ${fmtSpan(v.segment)}` : ""].filter(Boolean).join(" · ")}</span>
          {v.team ? (
            <Link to={`/teams/${v.team.id}`} className="pill mgr" data-testid="match-team-pill">{v.team.name}</Link>
          ) : (
            <span className="pill" data-testid="match-team-pill">Personal</span>
          )}
          {v.can_change_team && managedTeams.length > 0 && (
            <button
              type="button"
              className="tiny u"
              onClick={() => {
                setPickedTeamId(v.team?.id ?? managedTeams[0]?.id ?? "");
                setConfirmClearMove(false);
                setTeamSheetOpen(true);
              }}
              data-testid="change-video-team-btn"
            >
              {v.team ? "Change team" : "Link to team"}
            </button>
          )}
        </div>
        <h1 className="mt6">{v.title}</h1>
        <RunReviewPanel v={v} reload={reload} />
        <ErrorLine error={clips.error} />
        {desktop ? (
          <>
            <div className="mt24" style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 32, alignItems: "start" }}>
              <div>
                {player}
                <div className="row mt16">{actions}<span className="tiny" style={{ marginLeft: "auto" }}>Not a delivery? Right-click a clip to tell us.</span></div>
                {youtube}
              </div>
              <div style={{ paddingTop: 8 }} data-testid="players-summary">{summary}</div>
            </div>
            {players}
          </>
        ) : (
          <>
            <div className="mt24">{player}</div>
            <div className="grid2 mt10">{actions}</div>
            {youtube}
            {players}
            {status}
            <p className="tiny mt32">Something not a delivery? Long-press a clip to tell us.</p>
          </>
        )}
        {clips.data && clips.data.summary.flagged > 0 && <p className="tiny mt16">{plural(clips.data.summary.flagged, "clip")} marked "not a delivery" · <Link className="u" to={`/videos/${v.id}/clips?flagged=1`}>review</Link></p>}
        {plan === "free" && v.expires_at && <p className="tiny mt16">Clips are kept 7 days on {planName(plan)}.</p>}
      </div>
      {gate && <Gate onClose={() => setGate(false)} onKeep={() => setGate(false)} />}
      {teamSheetOpen && (
        <Sheet onClose={() => !teamBusy && setTeamSheetOpen(false)}>
          <div data-testid="video-team-sheet">
            <h2>{v.team ? "Move match to another team" : "Link match to a team"}</h2>
            <p className="sub mt6">Team managers can assign player groups and individual clips to @handles.</p>
            <select
              className="field mt16"
              value={pickedTeamId}
              onChange={(e) => {
                setPickedTeamId(e.target.value);
                setConfirmClearMove(false);
              }}
              data-testid="video-team-select"
            >
              <option value="">Personal (only me)</option>
              {managedTeams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            {confirmClearMove && (
              <div className="notice mt16" data-testid="confirm-clear-assignments">
                <div className="med">Existing @handle assignments will be cleared</div>
                <p className="tiny mt4">Moving this match away from {v.team?.name} removes its current team @handle assignments.</p>
              </div>
            )}
            <div className="mt20">
              <button
                type="button"
                className="btn dark full"
                disabled={teamBusy}
                onClick={() => changeVideoTeam(confirmClearMove)}
                data-testid="video-team-save"
              >
                {teamBusy ? "Saving…" : confirmClearMove ? "Clear assignments & move" : "Save"}
              </button>
            </div>
            <div className="mt10">
              <button type="button" className="btn ghost full" onClick={() => setTeamSheetOpen(false)} disabled={teamBusy}>
                Cancel
              </button>
            </div>
          </div>
        </Sheet>
      )}
      {toast}
    </Shell>
  );
}
